import { cn } from "@/lib/utils";
import type { DayPoint } from "@/features/insights/utils/week-stats";

type Metric = "mood" | "energy" | "stress";

const METRIC_META: Record<
  Metric,
  { label: string; max: number; barClass: string }
> = {
  mood: { label: "Humör", max: 10, barClass: "bg-primary" },
  energy: { label: "Energi", max: 10, barClass: "bg-secondary" },
  stress: { label: "Stress", max: 10, barClass: "bg-accent" },
};

export function WeekChart({
  days,
  metric,
}: {
  days: DayPoint[];
  metric: Metric;
}) {
  const meta = METRIC_META[metric];
  const n = days.length;
  // Thin labels on dense charts so they stay readable
  const labelEvery =
    n <= 7 ? 1 : n <= 14 ? 2 : n <= 31 ? 5 : Math.max(1, Math.floor(n / 6));

  return (
    <div className="space-y-3">
      <div className="flex h-36 items-end justify-between gap-0.5 px-0.5 sm:gap-1">
        {days.map((d, i) => {
          const raw = d[metric];
          const has = raw != null && d.count > 0;
          const heightPct = has
            ? Math.max(8, Math.round((raw / meta.max) * 100))
            : 0;
          const showLabel =
            i === 0 ||
            i === n - 1 ||
            i % labelEvery === 0;
          return (
            <div
              key={d.dateKey}
              className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
            >
              <div className="relative flex w-full flex-1 items-end justify-center">
                {has ? (
                  <div
                    className={cn(
                      "w-[70%] max-w-8 rounded-t-md transition-all sm:rounded-t-lg",
                      meta.barClass,
                      "opacity-85"
                    )}
                    style={{ height: `${heightPct}%` }}
                    title={`${d.dateKey}: ${meta.label} ${Math.round(raw * 10) / 10}`}
                  />
                ) : (
                  <div
                    className="w-[70%] max-w-8 rounded-t-md bg-surface-muted sm:rounded-t-lg"
                    style={{ height: "8%" }}
                    aria-hidden
                  />
                )}
              </div>
              <span
                className={cn(
                  "max-w-full truncate text-center text-[9px] font-medium text-foreground-subtle sm:text-[10px]",
                  !showLabel && "opacity-0"
                )}
              >
                {showLabel ? d.label : "·"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MetricToggle({
  value,
  onChange,
  labels,
}: {
  value: Metric;
  onChange: (m: Metric) => void;
  labels?: Record<Metric, string>;
}) {
  const items: Metric[] = ["mood", "energy", "stress"];
  const resolved: Record<Metric, string> = labels ?? {
    mood: "Humör",
    energy: "Energi",
    stress: "Stress",
  };
  return (
    <div className="flex gap-2">
      {items.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={cn(
            "min-h-10 flex-1 rounded-xl px-2 py-2 text-xs font-medium transition-colors",
            value === m
              ? "bg-primary text-primary-foreground"
              : "bg-surface-muted text-foreground-muted"
          )}
        >
          {resolved[m]}
        </button>
      ))}
    </div>
  );
}

export function RangeToggle({
  value,
  onChange,
  options,
}: {
  value: 7 | 30 | 90;
  onChange: (r: 7 | 30 | 90) => void;
  options: Array<{ value: 7 | 30 | 90; label: string }>;
}) {
  return (
    <div className="flex gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            "min-h-9 flex-1 rounded-full px-2 py-1.5 text-xs font-medium transition-colors",
            value === opt.value
              ? "bg-primary-soft text-primary"
              : "bg-surface-muted text-foreground-muted"
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
